import React from 'react';
import { UIBadge, UIUser, UserPercentileBucket } from 'types';
import { Button } from 'flowbite-react';
import { HiArrowLeft, HiOutlineDownload } from 'react-icons/hi';
import { useMediaQuery } from '@mui/material';
import { PiShareNetworkBold } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProfileExpEduLoc from './ProfileExpEduLoc';
import { copyToClipboard } from 'src/utils/copyToClipboard.utils';
import { APP_ENDPOINT, isRecruiterSide } from 'src/config/env.config';
import classNames from 'classnames';
import { sendTrackingEvent } from 'src/utils/gtm';
import { BiPencil } from 'react-icons/bi';
import MatchPercentageCircularBarComponentProfilePage from '../recruiterPositions/MatchPercentageCircularBarComponentProfilePage';
import { useUserContext } from 'src/context/UserProvider';
import useGetLogo from 'src/utils/useGetLogo';
import GeneratedSummary from './GeneratedSummary';
import ProfileLevelProgress from './ProfileLevelProgress';
import ProfileSocial from './ProfileSocial';
import { isScoreRangeValid } from 'shared-utils';

interface Props {
	isSelf?: boolean;
	showBannerBg?: boolean;
	userProfileInfo: UIUser;
	matchPercentage?: number;
	forceMobileView?: boolean;
	hasShownInterest?: boolean;
	showingToPublic?: boolean;
	userPercentileBucketData?: UserPercentileBucket;
}

const ProfileIntroduction = ({
	userProfileInfo,
	isSelf,
	matchPercentage,
	hasShownInterest,
	forceMobileView,
	showBannerBg = true,
	showingToPublic,
	userPercentileBucketData,
}: Props) => {
	const getRankBadge = (userPercentileBucketData: UserPercentileBucket) => {
		if (userPercentileBucketData === UserPercentileBucket.TOP_ONE) {
			return '/assets/top1.svg';
		}
		if (userPercentileBucketData === UserPercentileBucket.TOP_TEN) {
			return '/assets/top10.svg';
		}
		if (userPercentileBucketData === UserPercentileBucket.TOP_TWENTY_FIVE) {
			return '/assets/top25.svg';
		}
		if (userPercentileBucketData === UserPercentileBucket.TOP_FIFTY) {
			return '/assets/top50.svg';
		}
	};
	const navigate = useNavigate();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [searchParams, setSearchParams] = useSearchParams();
	const { user, isJobseekerLoggedIn } = useUserContext();
	// Accessing individual query parameters
	const { logo } = useGetLogo();

	const badgesList: UIBadge[] = [
		{
			title: 'Expert',
			skill: 'Top 1% of users',
			maxScore: 1000,
			minScore: 750,
			scoreRange: 'Top 1% of users',
		},
		{
			title: 'Advanced',
			skill: 'Top 10% of users',
			maxScore: 750,
			minScore: 500,
			scoreRange: 'Top 10% of users',
		},
		{
			title: 'Intermediate',
			skill: '',
			maxScore: 500,
			minScore: 250,
			scoreRange: 'Top 25% of users',
		},
		{
			title: 'Beginner',
			skill: '',
			maxScore: 250,
			minScore: 1,
			scoreRange: 'Top 50% of users',
		},
	];

	const getBadgesWithSkills = () => {
		const badgesWithSkills = badgesList.map(badge => {
			const filteredSkills = (
				userProfileInfo?.evaluation?.skillRatings?.length ? userProfileInfo?.evaluation?.skillRatings || [] : []
			).filter(item => {
				const score = isScoreRangeValid(Math.round(item.rating));
				return score >= badge.minScore && score < badge.maxScore;
			});
			return { ...badge, skills: filteredSkills };
		});

		// Sort badges by the number of skills, descending
		return badgesWithSkills?.sort((a, b) => b.minScore - a.minScore)?.filter(el => el.skills.length);
	};

	return (
		<div className=" w-full @container/profile-introduction px-4 lg:px-0">
			{/* Profile Avatar, Name, experience ... */}
			<div
				className={classNames(
					'isolate w-full flex flex-col items-center py-4 justify-center min-h-[200px] relative overflow-hidden',
					{
						'min-h-max !py-0': !showBannerBg && !isMobile,
					},
				)}
			>
				<div
					className={classNames('w-full gap-1 flex items-center justify-start pb-4 md:pb-0 max-w-[1023px] mx-auto', {
						'!pb-0 absolute !px-2 top-0 z-[1]': !showBannerBg && !isMobile,
						'px-2': forceMobileView || isMobile,
						'!pb-2': user,
						'!px-0': user,
					})}
				>
					{isRecruiterSide ? (
						<button
							className="flex md:hidden items-center gap-3 cursor-pointer mr-auto text-base font-[500]"
							onClick={() => {
								searchParams.delete('handler');
								setSearchParams(searchParams);
							}}
						>
							<HiArrowLeft />
							All Candidates
						</button>
					) : null}

					{!isSelf ? (
						<>
							{/* when user is signed in and is on someone else's profile page */}
							{user && isJobseekerLoggedIn && (
								<div className="flex gap-2 max-w-[1023px]">
									<Button color="light" size="lg" onClick={() => navigate('/')}>
										<HiArrowLeft />
									</Button>
								</div>
							)}
						</>
					) : null}
				</div>

				<div
					className={classNames(
						'bg-white rounded-[8px] relative dark:bg-[#1F2A37] w-full  max-w-[1024px] mx-auto px-[10px] md:px-6 py-[20px] md:py-[30px]',
					)}
				>
					<div className="flex gap-[10px] md:gap-5 w-full">
						<div className="absolute w-[110.171px] md:w-[110.171px] h-[48px] right-[24px] hidden @3xl/profile-introduction:block">
							<img src={logo} alt="logo" />
						</div>

						<div className="w-[72px] md:w-[120px]">
							<MatchPercentageCircularBarComponentProfilePage
								isSelf={isSelf}
								showLarge={true}
								value={matchPercentage || 0}
								userName={userProfileInfo.name}
								imageUrl={userProfileInfo.profile.proctoringImageUrl || ''}
							/>
						</div>

						<div>
							<ProfileExpEduLoc userProfileInfo={userProfileInfo} isSelf={isSelf} hasShownInterest={hasShownInterest} />
							<div className="hidden md:block mt-3">
								<ProfileSocial userProfileInfo={userProfileInfo} isSelf={!!isSelf} showBannerBg={!!showBannerBg} />
							</div>
						</div>
					</div>

					<div className="flex flex-wrap gap-2 md:gap-0 justify-between items-end mt-[10px] md:mt-3">
						{userProfileInfo?.profile?.userAiSummary?.aiSummary ? (
							<GeneratedSummary userProfileInfo={userProfileInfo} isSelf={!!isSelf} />
						) : null}
						{/* <div className="absolute @3xl/profile-introduction:static top-[20px] right-[12px] ml-auto">
							{showingToPublic ? (
								<>
									{userPercentileBucketData ? (
										<div className="w-[60px] h-[60px] md:w-[80px] md:h-[80px]">
											<img src={getRankBadge(userPercentileBucketData)} alt="rank" className="w-full h-full" />
										</div>
									) : null}
								</>
							) : (
								<ProfileLevelProgress userProfileInfo={userProfileInfo} isSelf={!!isSelf} />
							)}
						</div> */}
					</div>

					<div className="md:hidden mt-4">
						<ProfileSocial userProfileInfo={userProfileInfo} isSelf={!!isSelf} showBannerBg={!!showBannerBg} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProfileIntroduction;
